import React, { useContext, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import {
  CountryCode, GlobalProcessDuration, GlobalVisaType, Vendor,
} from 'API';
import GeoPicker from 'components/forms/GeoPicker';
import VendorCoCounselPicker from 'components/forms/VendorCoCounselPicker';
import IconCircle from 'components/IconCircle';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { DICTIONARY } from 'shared/constants';
import { resolvers } from 'shared/enums';

const durationResolver = resolvers.GlobalProcessDuration;
const visaTypeResolver = resolvers.GlobalVisaType;

const GlobalProcessFields: React.FC = () => {
  const { process, setProcess } = useContext(CreateProcessContext);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  const handleDurationChange = (event: any) => {
    const { value } = event.target;
    setProcess((draft) => {
      draft.globalProcessDuration = value;
      draft.globalProcessCountryCode = null;
      draft.globalVisaType = null;
      draft.vendorID = null;
    });
    setSelectedVendor(null);
  };

  const handleCountryChange = (event: any, value: any) => {
    setProcess((draft) => {
      draft.globalProcessCountryCode = value;
    });
  };

  const handleVisaTypeChange = (event: any) => {
    const { value } = event.target;
    setProcess((draft) => {
      draft.globalVisaType = value;
    });
  };

  const handleVendorChange = (vendor: Vendor | null) => {
    setSelectedVendor(vendor);
    setProcess((draft) => {
      draft.vendorID = vendor?.id || null;
    });
  };

  return (
    <Box sx={{ marginTop: '1rem' }}>
      <IconRow>
        <IconRowCell type="icon">
          <IconCircle Icon={PublicIcon} />
        </IconRowCell>
        <IconRowCell type="text">
          <Typography variant="h6">{DICTIONARY.TypeOfProcess}</Typography>
        </IconRowCell>
      </IconRow>

      <IconRow isInput sx={{ marginBottom: '1rem' }}>
        <IconRowCell type="text">
          <RadioGroup
            aria-label="global-process-duration"
            name="global-process-duration"
            value={process.globalProcessDuration || null}
            onChange={handleDurationChange}
          >
            <FormControlLabel
              value={GlobalProcessDuration.SHORT_TERM_BUSINESS_VISA}
              control={<Radio color="primary" />}
              label={durationResolver(GlobalProcessDuration.SHORT_TERM_BUSINESS_VISA)}
            />
            <FormControlLabel
              value={GlobalProcessDuration.LONG_TERM_EMPLOYMENT}
              control={<Radio color="primary" />}
              label={durationResolver(GlobalProcessDuration.LONG_TERM_EMPLOYMENT)}
            />
          </RadioGroup>
        </IconRowCell>
      </IconRow>

      {process.globalProcessDuration === GlobalProcessDuration.SHORT_TERM_BUSINESS_VISA && (
        <>
          <IconRow>
            <IconRowCell type="text">
              <Typography variant="subtitle1">{DICTIONARY.VisaInformation}</Typography>
            </IconRowCell>
          </IconRow>
          <IconRow isInput sx={{ marginBottom: '1rem' }}>
            <IconRowCell type="text">
              <GeoPicker
                type="country"
                label={DICTIONARY.DestinationCountry}
                value={(process.globalProcessCountryCode as CountryCode) || null}
                onChange={handleCountryChange}
              />
            </IconRowCell>
          </IconRow>
          <IconRow isInput sx={{ marginBottom: '1rem' }}>
            <IconRowCell type="text">
              <FormControl fullWidth>
                <InputLabel htmlFor="global-visa-type-select" required>
                  {DICTIONARY.TypeOfVisa}
                </InputLabel>
                <Select
                  id="global-visa-type-select"
                  value={process.globalVisaType || ''}
                  onChange={handleVisaTypeChange}
                  required
                >
                  {Object.values(GlobalVisaType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {visaTypeResolver(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </IconRowCell>
          </IconRow>
        </>
      )}

      {process.globalProcessDuration === GlobalProcessDuration.LONG_TERM_EMPLOYMENT && (
        <>
          <IconRow>
            <IconRowCell type="text">
              <Typography variant="subtitle1">{DICTIONARY.CoCounselInformation}</Typography>
            </IconRowCell>
          </IconRow>
          <IconRow isInput sx={{ marginBottom: '1rem' }}>
            <IconRowCell type="text">
              <VendorCoCounselPicker
                value={selectedVendor}
                onChange={handleVendorChange}
              />
            </IconRowCell>
          </IconRow>
        </>
      )}
    </Box>
  );
};

export default GlobalProcessFields;
